import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Alert, Spinner, Dropdown } from 'react-bootstrap';
import { BsGoogle, BsChevronRight, BsPlusCircle, BsArrowRepeat, BsTrash, BsFiles, BsExclamationTriangle } from 'react-icons/bs';
import { useWorkspace } from '../../../Context/WorkspaceContext';
import './AddGoogleDocsModal.css';
import '../agent-training.css';
import { useGlobal } from '../../../Context/GlobalContext';
import { CancelButton, PrimaryButton, OutlinedPrimaryButton } from '../../../Components/Buttons';
import { useLanguage } from '../../../Context/LanguageContext';

const AddGoogleDocsModal = ({ show, onHide, workspaceId, apiUrl, onPollState }) => {
  const { t } = useLanguage();
  const { currentWorkspace } = useWorkspace();
  const { googleApiKey, googleClientId } = useGlobal();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [error, setError] = useState(null);
  const [needsGoogleAuth, setNeedsGoogleAuth] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [connectedEmail, setConnectedEmail] = useState(null);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
  const [isFetchingToken, setIsFetchingToken] = useState(false);

  // Add confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  // Load Google API script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('picker:client', () => {
        setPickerApiLoaded(true);
      });
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Check for connected email when modal opens
  useEffect(() => {
    if (show) {
      const storedEmail = localStorage.getItem('temp_connected_email');
      if (storedEmail) {
        setConnectedEmail(storedEmail);
        localStorage.removeItem('temp_connected_email');
      }

      const urlParams = new URLSearchParams(window.location.search);
      const emailParam = urlParams.get('connected_email');
      if (emailParam && !storedEmail) {
        setConnectedEmail(emailParam);
      }

      setAccountsLoaded(false);
      fetchConnectedAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Handle account selection after accounts load or connectedEmail changes
  useEffect(() => {
    if (!accountsLoaded || connectedAccounts.length === 0) {
      return;
    }

    let accountToSelect = null;
    if (connectedEmail) {
      accountToSelect = connectedAccounts.find(
        account => account.email === connectedEmail
      );
    }

    // Fallback to the first account if no match or no connected email
    if (!accountToSelect) {
      accountToSelect = connectedAccounts[0];
    }

    setCurrentAccount(accountToSelect);
    setConnectedEmail(null); // Reset after use

  }, [accountsLoaded, connectedAccounts, connectedEmail]);

  // Fetch connected Google accounts
  const fetchConnectedAccounts = async () => {
    setError(null);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      // Get workspace_id from WorkspaceContext
      const workspace_id = currentWorkspace?.workspace_id;

      if (!workspace_id) {
        setError(t('dashboard.training.modals.addgoogledocsmodal.errorNoWorkspace'));
        setNeedsGoogleAuth(true);
        return;
      }

      const response = await fetch(`${apiUrl}/api/v1/google/accounts?workspace_id=${workspace_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        const accounts = data.accounts || [];

        setConnectedAccounts(accounts);
        setNeedsGoogleAuth(accounts.length === 0);
        setAccountsLoaded(true);
      } else {
        setError(data.detail || t('dashboard.training.modals.addgoogledocsmodal.errorFetchAccounts'));
        setNeedsGoogleAuth(true);
      }
    } catch (err) {
      setError(t('dashboard.training.modals.addgoogledocsmodal.errorFetchAccounts'));
      setNeedsGoogleAuth(true);
    }
  };

  const startTrainingWithDocs = useCallback(async (docIds) => {
    if (!docIds || docIds.length === 0) {
      return;
    }
    if (!currentAccount) {
      setError(t('dashboard.training.modals.addgoogledocsmodal.errorNoAccount'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      const response = await fetch(`${apiUrl}/api/v1/google/docs/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          doc_ids: docIds,
          account_id: currentAccount.account_id
        }),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.detail || t('dashboard.training.modals.addgoogledocsmodal.errorStartTraining'));
      }

      onPollState();
    } catch (err) {
      // Since the modal is hidden, we can't show the error there.
      // A global notification system would be ideal here.
      // For now, we log it and a user might see it if they reopen a modal.
      setError(err.message || t('dashboard.training.modals.addgoogledocsmodal.errorSubmitDocuments'));
    } finally {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, apiUrl, onPollState, currentAccount]);

  // Callback for Google Picker
  const pickerCallback = useCallback((data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const docIds = data.docs.map(doc => doc.id);
      if (docIds.length > 0) {
        startTrainingWithDocs(docIds);
      }
    }
  }, [startTrainingWithDocs]);

  // Function to create and show the Google Picker
  const showPicker = useCallback((accessToken) => {
    if (!pickerApiLoaded || !accessToken) {
      setError(t('dashboard.training.modals.addgoogledocsmodal.errorPickerNotReady'));
      return;
    }

    const view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
    view.setMimeTypes("application/vnd.google-apps.document");

    const picker = new window.google.picker.PickerBuilder()
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setAppId(googleClientId)
      .setOAuthToken(accessToken)
      .addView(view)
      .setDeveloperKey(googleApiKey)
      .setCallback(pickerCallback)
      .build();
    picker.setVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerApiLoaded, pickerCallback, googleClientId, googleApiKey]);

  const handleShowPicker = async () => {
    if (!currentAccount) return;

    setIsFetchingToken(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const workspace_id = currentWorkspace?.workspace_id;

      const response = await fetch(`${apiUrl}/api/v1/google/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: currentAccount.account_id,
          workspace_id: workspace_id
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('dashboard.training.modals.addgoogledocsmodal.errorRefreshToken'));
      }

      onHide(); // Close the modal before showing the picker
      showPicker(data.access_token);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsFetchingToken(false);
    }
  };

  // Handle Google Auth
  const handleGoogleAuth = () => {
    setIsAuthenticating(true);
    localStorage.setItem('googleAuthRedirectUrl', window.location.href);
    // Get workspace_id from context
    const workspace_id = currentWorkspace?.workspace_id;
    const brandvoxToken = localStorage.getItem('brandvox_token');

    // Create the auth request parameters
    const authParams = new URLSearchParams({
      state: `add_account:${workspaceId}`,
      workspace_id: workspace_id,
      brandvox_token: brandvoxToken
    });

    window.location.href = `${apiUrl}/api/v1/google/auth/authorize?${authParams.toString()}`;
  };

  // Switch Google account
  const handleAccountSwitch = (account) => {
    setCurrentAccount(account);
    setSelectedDocs([]);
  };

  // Handle Google Account Revocation
  const handleRevokeAccount = async (account) => {
    showConfirm('revokeAccount', account);
  };

  const handleRevokeAccountConfirmed = async (account) => {
    setIsRevoking(true);
    setError(null);
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      // Get workspace_id from WorkspaceContext
      const workspace_id = currentWorkspace?.workspace_id;

      const response = await fetch(`${apiUrl}/api/v1/google/accounts/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: account.account_id,
          workspace_id: workspace_id
        }),
      });

      if (response.ok) {
        // After revoking, refetch accounts. The useEffect will handle resetting the current account.
        await fetchConnectedAccounts();
      } else {
        const data = await response.json();
        setError(data.detail || t('dashboard.training.modals.addgoogledocsmodal.errorRevokeAccount'));
      }
    } catch (err) {
      setError(t('dashboard.training.modals.addgoogledocsmodal.errorRevokeAccount'));
    } finally {
      setIsRevoking(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedDocs.length === 0) {
      setError(t('dashboard.training.modals.addgoogledocsmodal.errorSelectDocument'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      const response = await fetch(`${apiUrl}/api/v1/google/docs/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          doc_ids: selectedDocs.map(doc => doc.id),
          account_id: currentAccount.account_id
        }),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(responseData.detail || t('dashboard.training.modals.addgoogledocsmodal.errorAddDocuments'));
      }

      onPollState();
      onHide();
    } catch (err) {
      setError(err.message || t('dashboard.training.modals.addgoogledocsmodal.errorSubmitDocuments'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render account selector
  const renderAccountSelector = () => {
    if (connectedAccounts.length === 0) return null;

    const currentAccountNeedsReconnection = currentAccount?.needs_reconnection;

    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center account-selector-container">
          <Dropdown className="account-selector-dropdown">
            <Dropdown.Toggle variant="outline-secondary" id="account-dropdown" className="d-flex align-items-center account-toggle">
              <img
                src={currentAccount?.picture}
                alt=""
                className="rounded-circle me-2 account-avatar"
              />
              <span className="text-truncate account-email">{currentAccount?.email}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="account-dropdown-menu">
              {connectedAccounts.map(account => (
                <div key={account.account_id} className="account-item px-2 py-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <Dropdown.Item
                      onClick={() => handleAccountSwitch(account)}
                      active={currentAccount?.account_id === account.account_id}
                      className="d-flex align-items-center account-select-item"
                    >
                      <img
                        src={account.picture}
                        alt=""
                        className="rounded-circle me-2 account-avatar"
                      />
                      <span className="text-truncate flex-grow-1">{account.email}</span>
                      {account.needs_reconnection && (
                        <BsExclamationTriangle className="text-warning ms-2" />
                      )}
                    </Dropdown.Item>
                    <button
                      onClick={() => handleRevokeAccount(account)}
                      className="btn btn-sm btn-danger disconnect-btn ms-2"
                      disabled={isRevoking && confirmData?.account_id === account.account_id}
                      title={t('dashboard.training.modals.addgoogledocsmodal.disconnectButton')}
                    >
                      {isRevoking && confirmData?.account_id === account.account_id ? (
                        <Spinner size="sm" />
                      ) : (
                        <BsTrash />
                      )}
                    </button>
                  </div>
                </div>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleGoogleAuth} className="d-flex align-items-center">
                <BsPlusCircle className="me-2" />
                {t('dashboard.training.modals.addgoogledocsmodal.addAnotherAccount')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Show reconnection warning and button if current account needs reconnection */}
        {currentAccountNeedsReconnection && (
          <Alert variant="warning" className="mt-3 mb-0 reconnection-alert">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-start">
                <BsExclamationTriangle className="me-2 mt-1" />
                <div>
                  <strong>{t('dashboard.training.modals.addgoogledocsmodal.connectionIssueTitle')}</strong>
                  <p className="mb-0 small">
                    {currentAccount.validation_error || t('dashboard.training.modals.addgoogledocsmodal.connectionIssueDefault')}
                  </p>
                </div>
              </div>
              <OutlinedPrimaryButton
                onClick={handleGoogleAuth}
                className="ms-3 reconnect-btn"
              >
                <BsArrowRepeat className="me-2" />
                {t('dashboard.training.modals.addgoogledocsmodal.reconnectButton')}
              </OutlinedPrimaryButton>
            </div>
          </Alert>
        )}
      </div>
    );
  };

  // Function to show confirmation modal
  const showConfirm = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };

  // Get confirmation message based on action
  const getConfirmMessage = () => {
    if (confirmAction === 'revokeAccount') {
      return t('dashboard.training.modals.addgoogledocsmodal.confirmDisconnectMessage', { email: confirmData?.email });
    }
    return '';
  };

  // Handle confirmation
  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction === 'revokeAccount') {
      await handleRevokeAccountConfirmed(confirmData);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered className="add-google-docs-modal" enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>{t('dashboard.training.modals.addgoogledocsmodal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {needsGoogleAuth ? (
            <div className="auth-section">
              <BsGoogle size={48} className="google-icon" />
              <h5>{t('dashboard.training.modals.addgoogledocsmodal.connectTitle')}</h5>
              <p className="text-muted mb-4">
                {t('dashboard.training.modals.addgoogledocsmodal.connectDescription')}
              </p>
              <PrimaryButton
                backgroundColor="var(--google-primary)"
                color="white"
                hoverBackgroundColor="var(--google-primary)"
                hoverColor="white"
                hoverShadow="none"
                width="100%"
                onClick={handleGoogleAuth}
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  <Spinner size="sm" />
                ) : (
                  <BsGoogle size={16} />
                )}
                <span className="ms-2">{t('dashboard.training.modals.addgoogledocsmodal.connectButton')}</span>
              </PrimaryButton>
            </div>
          ) : (
            <Form onSubmit={handleSubmit} className="form-content">
              {renderAccountSelector()}
              {!currentAccount?.needs_reconnection && (
                <div className="picker-button-container">
                  <OutlinedPrimaryButton
                    color="var(--google-primary)"
                    hoverBackgroundColor="var(--google-primary)"
                    hoverColor="white"
                    hoverShadow="none"
                    fontSize="20px"
                    width="100%"
                    height="100%"
                    onClick={handleShowPicker}
                    disabled={!pickerApiLoaded || !currentAccount || isFetchingToken}
                  >
                    {isFetchingToken ? <Spinner size="sm" className="me-2" /> : <BsFiles className="me-2" />}
                    {t('dashboard.training.modals.addgoogledocsmodal.selectFilesButton')}
                  </OutlinedPrimaryButton>
                </div>
              )}
            </Form>
          )}
        </Modal.Body>
        {!needsGoogleAuth && (
          <Modal.Footer>
            <CancelButton variant="secondary" className="cancel-btn" onClick={onHide}>
              {t('dashboard.training.modals.addgoogledocsmodal.cancelButton')}
            </CancelButton>
            <PrimaryButton
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || selectedDocs.length === 0}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('dashboard.training.modals.addgoogledocsmodal.addingButton')}
                </>
              ) : (
                <>
                  {t('dashboard.training.modals.addgoogledocsmodal.addButton')}
                  <BsChevronRight className="ms-2" />
                </>
              )}
            </PrimaryButton>
          </Modal.Footer>
        )}
      </Modal>

      {/* Confirmation modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold text-danger">
            {t('dashboard.training.modals.addgoogledocsmodal.confirmDisconnectTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="d-flex">
            <div className="confirm-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
              </svg>
            </div>
            <p className="mb-0">{getConfirmMessage()}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <CancelButton
            variant="lighter"
            onClick={() => setShowConfirmModal(false)}
            className="me-2 hover-lift cancel-btn"
          >
            {t('dashboard.training.modals.addgoogledocsmodal.cancelButton')}
          </CancelButton>
          <PrimaryButton
            variant="danger"
            onClick={handleConfirm}
            className="px-4 hover-lift"
          >
            <span className="d-flex align-items-center">
              <BsTrash className="me-2" />
              {t('dashboard.training.modals.addgoogledocsmodal.disconnectButton')}
            </span>
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddGoogleDocsModal; 